/**
 * Admin → Access tab. Manages the temporary "Coming Soon" splash gate,
 * the public-launch toggle, and admin email notifications for new user
 * signups. Pure UI; parent Admin.jsx owns state, load fns, and handlers.
 */
export default function AdminAccessTab({
  siteConfig,
  accessCodes,
  newAccess,
  setNewAccess,
  setLandingMode,
  toggleNotifyNewUsers,
  createAccessCode,
  revokeAccessCode,
  deleteAccessCode,
  copyToClipboard,
}) {
  return (
    <div className="mt-6 space-y-4">
      {/* Launch Mode toggle */}
      <div className="h57-surface p-4 border-[#FF5F1F]/40">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Launch Mode</div>
            <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
              Currently:{" "}
              {siteConfig?.landing_mode === "public" ? (
                <span className="text-[#FF5F1F] font-semibold uppercase">Public — the &ldquo;Coming Soon&rdquo; splash is OFF. Visitors land straight on the HUNT57 home page.</span>
              ) : (
                <span className="text-white font-semibold uppercase">Invite-only — the temporary &ldquo;Coming Soon&rdquo; splash gates every visitor.</span>
              )}
              . Switch to <span className="text-white">Public</span> when you&apos;re ready for true launch — the splash disappears and visitors go straight to the Sign Up / Sign In landing page. Flip back to invite-only anytime.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => setLandingMode("invite_only")}
              disabled={siteConfig?.landing_mode === "invite_only"}
              data-testid="admin-launch-invite"
              className={`h57-btn-secondary text-xs whitespace-nowrap ${siteConfig?.landing_mode === "invite_only" ? "!border-[#FF5F1F] !text-[#FF5F1F]" : ""} disabled:cursor-default`}
            >
              Invite-only
            </button>
            <button
              onClick={() => setLandingMode("public")}
              disabled={siteConfig?.landing_mode === "public"}
              data-testid="admin-launch-public"
              className={`h57-btn-primary text-xs whitespace-nowrap ${siteConfig?.landing_mode === "public" ? "!border-[#FFD700] !bg-[#FFD700]/20 !text-[#FFD700]" : ""} disabled:cursor-default`}
            >
              🚀 Public Launch
            </button>
          </div>
        </div>
      </div>

      {/* Admin email notifications */}
      <div className="h57-surface p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Admin Email Notifications</div>
            <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
              When ON, every new user registration triggers a summary email
              (username, display name, email, signup date, state, invite code used, etc.)
              sent to <span className="text-white font-mono">NEW_USER_NOTIFY_INBOX</span>
              {" "}(or <span className="text-white font-mono">SUPPORT_INBOX</span> as fallback).
            </p>
            <p className="mt-1 text-xs text-zinc-500">
              Current status: <span className={siteConfig?.notify_new_users ? "text-emerald-400" : "text-zinc-500"}>
                {siteConfig?.notify_new_users ? "ON — you will receive an email for every new signup." : "OFF — no signup emails."}
              </span>
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm select-none shrink-0" data-testid="admin-notify-toggle-label">
            <input
              type="checkbox"
              checked={!!siteConfig?.notify_new_users}
              onChange={(e) => toggleNotifyNewUsers(e.target.checked)}
              data-testid="admin-notify-new-users"
              className="w-4 h-4"
            />
            <span className={siteConfig?.notify_new_users ? "text-emerald-400 font-semibold" : "text-zinc-400"}>
              {siteConfig?.notify_new_users ? "ON" : "OFF"}
            </span>
          </label>
        </div>
      </div>

      {/* Splash preview link */}
      <div className="h57-surface p-4 border-[#FFD700]/30">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700]">→ Site Gate — Coming Soon Splash</div>
            <p className="mt-2 text-sm text-zinc-400">
              Hunt57 is currently gated behind the temporary splash page. Codes below let people bypass it and reach the real landing page.
              Members who sign in with their account do not need a code.
            </p>
          </div>
          <a
            href="/?preview_gate=1"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-access-preview"
            className="h57-btn-secondary text-xs whitespace-nowrap self-start md:self-auto"
          >
            Preview Splash ↗
          </a>
        </div>
      </div>

      {/* Create code */}
      <div className="h57-surface p-4">
        <h3 className="font-display font-bold uppercase text-xs tracking-widest text-zinc-400 mb-3">Create Access Code</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <input
            className="h57-input font-mono uppercase tracking-widest"
            placeholder="CODE (auto if blank)"
            data-testid="admin-access-code"
            value={newAccess.code}
            onChange={(e) => setNewAccess({ ...newAccess, code: e.target.value })}
          />
          <input
            type="number"
            min="1"
            className="h57-input"
            placeholder="Max uses (blank = ∞)"
            data-testid="admin-access-max"
            value={newAccess.max_uses}
            onChange={(e) => setNewAccess({ ...newAccess, max_uses: e.target.value })}
          />
          <input
            className="h57-input"
            placeholder="Note (optional)"
            data-testid="admin-access-note"
            value={newAccess.note}
            onChange={(e) => setNewAccess({ ...newAccess, note: e.target.value })}
          />
          <button onClick={createAccessCode} className="h57-btn-primary" data-testid="admin-access-create">Create</button>
        </div>
      </div>

      {/* Codes table */}
      <div className="h57-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs text-zinc-500 uppercase tracking-widest border-b border-white/10">
            <tr>
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Uses</th>
              <th className="text-left p-3 hidden md:table-cell">Note</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accessCodes.map((c) => (
              <tr key={c.id} className="border-b border-white/5" data-testid={`access-row-${c.id}`}>
                <td className="p-3">
                  <button
                    onClick={() => copyToClipboard(c.code)}
                    className="font-mono text-xs text-white hover:text-[#FFD700] tracking-widest"
                    data-testid={`admin-access-copy-${c.id}`}
                    title="Click to copy"
                  >
                    {c.code}
                  </button>
                </td>
                <td className="p-3 font-mono text-xs">
                  {c.used_count || 0}{c.max_uses ? `/${c.max_uses}` : " / ∞"}
                </td>
                <td className="p-3 hidden md:table-cell text-zinc-400 text-xs max-w-xs truncate">{c.note || "—"}</td>
                <td className="p-3">
                  <span className={`h57-chip ${c.status === "revoked" ? "!border-red-500/50 !text-red-400" : ""}`}>{c.status}</span>
                </td>
                <td className="p-3 text-right space-x-1">
                  {c.status === "active" && (
                    <button onClick={() => revokeAccessCode(c.id)} data-testid={`admin-access-revoke-${c.id}`} className="h57-chip !py-1 !px-2 hover:!border-red-500">Revoke</button>
                  )}
                  <button onClick={() => deleteAccessCode(c.id)} data-testid={`admin-access-delete-${c.id}`} className="h57-chip !py-1 !px-2 !border-red-500/50 !text-red-400 hover:!border-red-500">Delete</button>
                </td>
              </tr>
            ))}
            {accessCodes.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-zinc-500 font-mono uppercase text-xs tracking-widest">No access codes yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
